import {IPaymentData} from "@connox/typescript-library";
import * as React from "react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {PaymentController, Payments} from "../../../lib/payments/PaymentController";
import PaymentBirthdayPicker from "./PaymentBirthdayPicker";
import Modal from "../../../lib/ModalWindow/Modal";
import {Button, ButtonStyles} from "../../../lib/form/Button/Button";

interface IPaymentRowProps {
  failedPaymentId?: number;
  onPaymentClicked: (paymentId: number) => void;
  payment: IPaymentData;
}

/**
 * Single Payment Row
 * @param props
 * @constructor
 */
const PaymentRow: React.FC<IPaymentRowProps> = (props: IPaymentRowProps) => {
  const {t} = useTranslation("checkout");
  const {failedPaymentId, onPaymentClicked, payment} = props;
  const {
    description,
    isCustomerPreferredPayment,
    isScoringRequired,
    name,
    paymentAvailability,
    paymentId,
    paymentDiscount,
  } = payment;
  const {discount, percent} = paymentDiscount;
  const isCreditCard = paymentId === Payments.CreditCardBraintree;
  const key = `payment${paymentId}`;

  const {hasExceededLimit, hasInvalidDeliveryAddress, isAvailable} = paymentAvailability;

  const isToBeBlurred: boolean = failedPaymentId === paymentId || hasExceededLimit || hasInvalidDeliveryAddress;
  const controllerPaymentId = PaymentController.getPaymentId();
  const isSelectedPayment =
    !isToBeBlurred &&
    isAvailable &&
    (paymentId === controllerPaymentId || (!controllerPaymentId && isCustomerPreferredPayment));

  const onClick = () => {
    if (!isAvailable) return;
    onPaymentClicked(paymentId);
  };

  const UnavailabilityInfo: React.FC = () => {
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    if (!isToBeBlurred) return null;
    const messages: string[] = [];
    if (hasExceededLimit) {
      messages.push(t("hasExceededLimit_Text", {paymentName: name, value: paymentAvailability.limit.formatted}));
    }
    if (hasInvalidDeliveryAddress) {
      messages.push(t("hasInvalidDeliveryAddress_Text", {paymentName: name}));
    }
    if (isToBeBlurred && !messages.length) {
      messages.push(
        t("notAvailable_Text", {
          paymentName: name,
        }),
      );
    }

    const onTextClick = (evt: React.MouseEvent<HTMLAnchorElement>) => {
      evt.preventDefault();
      setModalVisible(true);
    };

    return (
      <React.Fragment>
        <a href="#" onClick={onTextClick}>
          {t("Warum ist {{paymentName}} nicht verfügbar?", {paymentName: name})}
        </a>
        <Modal isVisible={modalVisible} closeFunction={() => setModalVisible(false)}>
          {messages.map((msg, index) => (
            <p key={`msg_${index}`} className="content">
              {msg}
            </p>
          ))}
          <p className="content">{t("Bitte wählen Sie eine andere Zahlungsart.")}</p>
          <Button styles={[ButtonStyles.big]} action={() => setModalVisible(false)}>
            {t("Okay")}
          </Button>
        </Modal>
      </React.Fragment>
    );
  };

  return (
    <div className={"SimpleRadioList__Row" + (isToBeBlurred ? " SimpleRadioList__Row--unavailable" : "")}>
      <label htmlFor={key}>
        <div className="SimpleRadioList__Row__Radio">
          <input
            type="radio"
            name="PaymentId"
            id={key}
            onChange={onClick}
            checked={isSelectedPayment && !isToBeBlurred}
            readOnly={isToBeBlurred}
            disabled={isToBeBlurred}
          />
        </div>
        <div className="SimpleRadioList__Row__Content">
          <p
            dangerouslySetInnerHTML={{
              __html:
                name +
                (Number(discount.amount) > 0
                  ? ` (${percent.formatted} - ${t("Sie sparen {{formatted}}", {formatted: discount.formatted})})`
                  : ""),
            }}
          />
          <UnavailabilityInfo />
          {isSelectedPayment && <p dangerouslySetInnerHTML={{__html: description}} />}
          {isSelectedPayment && isCreditCard && (
            <div className="credit-card-container">
              <label htmlFor="card-number" className="cc-label">
                {t("Kartennummer")}:
              </label>
              <div id="card-number" />
              <label htmlFor="cvv">{t("CVV")}:</label>
              <div id="cvv" />
              <label htmlFor="expiration-date">{t("Gültig bis")}:</label>
              <div id="expiration-date" />
              <div id="checkout-message" />
            </div>
          )}
          {!isToBeBlurred && (
            <PaymentBirthdayPicker isScoringRequired={isScoringRequired} isSelectedPayment={isSelectedPayment} />
          )}
        </div>
      </label>
    </div>
  );
};

export default PaymentRow;
